import React, { useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import {
  Play,
  Crown,
  ListOrdered,
  Gamepad2,
  BarChart3,
  Filter,
  ClipboardList,
  UserPlus,
  History,
  CalendarClock,
  Coins,
  X,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { useTranslation } from '../../hooks/use-i18n'
import { formatPoints } from '../../utils/points-label'
import { getMatchDate, formatDate } from '../../utils/dateHelper'
import TeamNameWithFlag from '../TeamNameWithFlag'
import VictoryTeamLabel from '../VictoryTeamLabel'
import { User, StartingPointsEntry } from '../../types/room'
import ViewPlayerStatsGamesModal from '../ViewPlayerStatsGamesModal'
import SetStartingPointsModal from '../SetStartingPointsModal'
import BestOfModal from '../BestOfModal'
import GamesAndPredictionsModal from '../GamesAndPredictionsModal'
import GamePredictionsModal from '../GamePredictionsModal'
import RoomRulesModal from '../RoomRulesModal'
import type { PlayedMatch } from '../../services/matchService'
import type { DemoRoomConfig } from '../../demo/demoRoomTypes'
import {
  DEMO_ROOM_ID,
  DEMO_ROOM_NAME,
  DEMO_PLAYERS,
  DEMO_PLAYED_MATCHES,
  DEMO_UPCOMING_MATCHES,
  DEMO_PLAY_MATCH,
  DEMO_BEST_PREDICTIONS,
  DEMO_MATCH_PREDICTIONS_BY_MATCH,
  DEMO_FUTURE_CHAMPION,
  DEMO_TOP_SCORER,
  getDemoPlayerPredictions,
  getDemoPlayerStats,
} from '../../demo/footballRoomDemoData'

const rankBadge = (rank: number) => {
  if (rank === 1) return '🥇'
  if (rank === 2) return '🥈'
  if (rank === 3) return '🥉'
  return null
}

/**
 * Admin demo only: mock starting-points adjustments per player, kept in sessionStorage so the
 * changes survive refresh/navigation within the same browser session (never persisted to a backend).
 */
type AdminPointsState = { entries: StartingPointsEntry[]; total: number }
const ADMIN_POINTS_STORAGE_KEY = 'brc-demo-admin-points'
// Stable empty reference so the player modal's effects don't loop on re-render.
const EMPTY_STARTING_ENTRIES: StartingPointsEntry[] = []

function loadDemoAdminPoints(): Record<string, AdminPointsState> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.sessionStorage.getItem(ADMIN_POINTS_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function saveDemoAdminPoints(data: Record<string, AdminPointsState>) {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(ADMIN_POINTS_STORAGE_KEY, JSON.stringify(data))
  } catch {
    /* ignore quota / serialization errors in demo */
  }
}

type FootballRoomDemoProps = {
  roomConfig?: DemoRoomConfig | null
  /** When true, show the room-owner admin controls (e.g. adjust starting points) with mock behavior. */
  adminMode?: boolean
}

const FootballRoomDemo: React.FC<FootballRoomDemoProps> = ({ roomConfig = null, adminMode = false }) => {
  const { t } = useTranslation()
  const roomName = roomConfig?.name ?? DEMO_ROOM_NAME
  const [showBestOf, setShowBestOf] = useState(false)
  const [showUpcomingList, setShowUpcomingList] = useState(false)
  const [showPlayedList, setShowPlayedList] = useState(false)
  const [showPlay, setShowPlay] = useState(false)
  const [showRulesModal, setShowRulesModal] = useState(false)
  const [selectedPlayer, setSelectedPlayer] = useState<User | null>(null)
  const [modalViewType, setModalViewType] = useState<'stats' | 'games' | 'upcoming'>('stats')
  const [selectedMatch, setSelectedMatch] = useState<PlayedMatch | null>(null)
  const [startingPointsPlayer, setStartingPointsPlayer] = useState<User | null>(null)
  // Admin demo: mocked starting-points adjustments, kept for the session only.
  const [adminPoints, setAdminPoints] = useState<Record<string, AdminPointsState>>(() =>
    adminMode ? loadDemoAdminPoints() : {},
  )

  const players = useMemo(() => {
    const base = DEMO_PLAYERS.map((p) => ({
      ...p,
      predictions: getDemoPlayerPredictions(p._id) as User['predictions'],
    }))
    if (!adminMode) return base
    // Admin demo starts every player at 0 points so added/removed points are easy to demonstrate.
    const withAdmin = base.map((p) => {
      const total = adminPoints[p._id]?.total ?? 0
      return { ...p, totalPoints: total, startingPoints: total }
    })
    // Re-rank by the admin-assigned total (stable for ties) like a real room.
    return withAdmin
      .map((p, i) => ({ p, i }))
      .sort((a, b) => (b.p.totalPoints ?? 0) - (a.p.totalPoints ?? 0) || a.i - b.i)
      .map((x) => x.p)
  }, [adminMode, adminPoints])

  const handleDemoStartingPoints = (target: User, signedAmount: number, note: string) => {
    setAdminPoints((prev) => {
      const cur = prev[target._id] ?? { entries: [], total: 0 }
      const entry: StartingPointsEntry = {
        id: `demo-sp-${Date.now()}`,
        amount: signedAmount,
        note: note || null,
        createdAt: new Date().toISOString(),
        setByName: t('predictionsPage.adminBadge'),
      }
      const next = {
        ...prev,
        [target._id]: { entries: [entry, ...cur.entries], total: cur.total + signedAmount },
      }
      saveDemoAdminPoints(next)
      return next
    })
  }

  const openPlayerModal = (player: User, viewType: 'stats' | 'games' | 'upcoming') => {
    setSelectedPlayer({
      ...player,
      predictions: getDemoPlayerPredictions(player._id) as User['predictions'],
    })
    setModalViewType(viewType)
  }

  const demoBundleForPlayer = (player: User | null) => {
    if (!player) return undefined
    const showFc = roomConfig?.setting.enableFutureChampionStart !== false
    const showTs = roomConfig?.setting.enableTopScorer ?? true
    return {
      stats: getDemoPlayerStats(player._id),
      futureChampionPredictions: showFc ? DEMO_FUTURE_CHAMPION : [],
      topScorerPredictions: showTs ? DEMO_TOP_SCORER : [],
      startingEntries: adminMode ? adminPoints[player._id]?.entries ?? EMPTY_STARTING_ENTRIES : EMPTY_STARTING_ENTRIES,
      startingTotal: adminMode ? adminPoints[player._id]?.total ?? 0 : 0,
    }
  }

  const demoRoomForRules = roomConfig
    ? {
        name: roomConfig.name,
        type: roomConfig.type,
        leagues: roomConfig.leagues,
        setting: roomConfig.setting as Record<string, string | boolean | undefined>,
      }
    : null

  const matchDemoPredictions = selectedMatch
    ? (DEMO_MATCH_PREDICTIONS_BY_MATCH[selectedMatch._id] ?? []).map((p, i) => ({
        _id: `demo-mp-${i}`,
        playerName: p.playerName,
        predictedScore: p.predictedScore,
        winner: p.winner,
        predictedWinnerPoint: p.resultPts,
        predictedScorePoint: p.scorePts,
        totalPoints: p.total,
      }))
    : undefined

  return (
    <>
      <div className="rounded-2xl border border-primary/20 bg-card overflow-hidden shadow-lg shadow-primary/5">
        <div className="px-4 sm:px-6 py-3 border-b border-border bg-primary/5 flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-display text-lg sm:text-xl font-bold text-foreground">
            👥 {t('predictionsPage.roomLeaderboard')} — {roomName}
          </h2>
          <span
            className={`text-xs sm:text-sm font-medium px-2.5 py-1 rounded-full border ${
              adminMode
                ? 'text-amber-300 bg-amber-500/10 border-amber-500/30'
                : 'text-primary/90 bg-primary/10 border-primary/20'
            }`}
          >
            {adminMode ? t('predictionsPage.adminBadge') : t('predictionsPage.demoBadge')}
          </span>
        </div>

        <div className="p-4 sm:p-6">
          <h3 className="font-display text-xl sm:text-2xl font-bold text-foreground mb-1">{roomName}</h3>
          <p className="text-small sm:text-body-lg text-muted-foreground mb-4 sm:mb-6">
            {t('roomPage.seasonActive', { count: String(players.length) })}
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-1.5 sm:gap-2 lg:gap-3 mb-6 sm:mb-8">
            <button
              type="button"
              onClick={() => setShowPlay(true)}
              className="py-2.5 sm:py-3.5 rounded-lg sm:rounded-xl bg-gradient-to-r from-green-900 to-green-500 text-white font-semibold text-xs sm:text-small lg:text-nav hover:opacity-90 transition-all flex items-center justify-center gap-1 sm:gap-2 min-w-0"
            >
              <Play size={14} className="sm:w-4 sm:h-4 shrink-0" /> {t('roomPage.play')}
            </button>
            <div className="grid grid-cols-3 gap-1.5 sm:gap-2 lg:contents">
              <button
                type="button"
                onClick={() => setShowBestOf(true)}
                className="py-2.5 sm:py-3.5 rounded-lg sm:rounded-xl bg-gradient-to-r from-warning to-destructive text-foreground font-semibold text-xs sm:text-small lg:text-nav hover:opacity-90 transition-all flex items-center justify-center gap-1 sm:gap-2 min-w-0"
              >
                <Crown size={14} className="sm:w-4 sm:h-4 shrink-0" /> {t('roomPage.bestOf')}
              </button>
              <button
                type="button"
                onClick={() => setShowPlayedList(true)}
                className="py-2.5 sm:py-3.5 rounded-lg sm:rounded-xl bg-gradient-to-r from-sky-800 to-sky-400 text-white font-semibold text-xs sm:text-small lg:text-nav hover:opacity-90 transition-all flex items-center justify-center gap-1 sm:gap-2 min-w-0"
              >
                <History size={14} className="sm:w-4 sm:h-4 shrink-0" /> {t('roomPage.played')}
              </button>
              <button
                type="button"
                onClick={() => setShowUpcomingList(true)}
                className="py-2.5 sm:py-3.5 rounded-lg sm:rounded-xl bg-gradient-to-r from-red-900 to-red-500 text-white font-semibold text-xs sm:text-small lg:text-nav hover:opacity-90 transition-all flex items-center justify-center gap-1 sm:gap-2 min-w-0"
              >
                <ListOrdered size={14} className="sm:w-4 sm:h-4 shrink-0" /> {t('roomPage.gamesAndPredictions')}
              </button>
            </div>
          </div>

          <div className="glass-card rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-6">
            <div className="flex items-center gap-1 sm:gap-1.5 mb-3 sm:mb-5 min-w-0">
              <h4 className="font-display text-base sm:text-lg font-semibold flex items-center gap-1.5 text-foreground shrink-0">
                🏆 {t('roomPage.ranking')}
              </h4>
              <button
                type="button"
                onClick={() => setShowRulesModal(true)}
                className="p-1.5 sm:p-2 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all shrink-0"
                title={t('roomPage.rules')}
              >
                <ClipboardList size={16} className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => toast(t('predictionsPage.demoInviteHint'), { icon: '👥' })}
                className="p-1.5 sm:p-2 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all shrink-0"
                title={t('roomPage.invitePlayer')}
              >
                <UserPlus size={16} className="w-4 h-4" />
              </button>
              <button type="button" className="p-1.5 sm:p-2 rounded-lg hover:bg-muted/50 text-muted-foreground shrink-0 ml-auto" aria-label="Filter">
                <Filter size={14} className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-2 sm:space-y-2.5">
              {players.map((player, index) => {
                const isTop3 = index < 3
                const colors = ['bg-primary', 'bg-destructive', 'bg-success', 'bg-warning', 'bg-chess', 'bg-primary', 'bg-destructive', 'bg-success', 'bg-warning']
                const color = colors[index % colors.length]
                return (
                  <div
                    key={player._id}
                    className={`rounded-lg sm:rounded-xl p-2.5 sm:p-3.5 lg:p-4 flex items-center gap-2 sm:gap-3 lg:gap-4 transition-all hover:bg-muted/30 ${
                      isTop3 ? 'glass-card border border-primary/10' : 'glass-card'
                    }`}
                  >
                    <span className="text-base sm:text-display-2 font-display font-bold w-6 sm:w-7 lg:w-8 text-center text-muted-foreground shrink-0">
                      {rankBadge(index + 1) || (index + 1)}
                    </span>
                    <div className={`w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full ${color} flex items-center justify-center text-[0.65rem] sm:text-small font-bold text-primary-foreground shrink-0`}>
                      {player.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-small sm:text-nav truncate text-foreground">{player.name}</p>
                      <div className="flex items-center gap-2 sm:gap-3 mt-0.5">
                        <span className="text-small sm:text-nav font-semibold text-primary">{formatPoints(player.totalPoints ?? 0, t)}</span>
                        <span className="text-small text-muted-foreground/60">·</span>
                        <span className="text-small text-muted-foreground">{player.predictionCount ?? 0} {t('roomPage.pred')}</span>
                      </div>
                    </div>
                    <div className="flex gap-0.5 sm:gap-1 lg:gap-1.5 shrink-0">
                      {adminMode && (
                        <button
                          type="button"
                          onClick={() => setStartingPointsPlayer(player)}
                          className="p-1.5 sm:px-2 sm:py-1.5 rounded-md sm:rounded-lg text-amber-400 text-small font-medium hover:opacity-80 transition-opacity flex items-center gap-1"
                          aria-label={t('roomPage.setStartingPoints')}
                          title={t('roomPage.setStartingPoints')}
                        >
                          <Coins size={14} className="shrink-0" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => openPlayerModal(player, 'games')}
                        className="p-1.5 sm:px-2 sm:py-1.5 rounded-md sm:rounded-lg text-sky-400 text-small font-medium hover:opacity-80 transition-opacity flex items-center gap-1"
                        aria-label={t('roomPage.played')}
                      >
                        <Gamepad2 size={14} className="shrink-0" /> <span className="hidden sm:inline">{t('roomPage.played')}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => openPlayerModal(player, 'upcoming')}
                        className="p-1.5 sm:px-2 sm:py-1.5 rounded-md sm:rounded-lg text-red-500 text-small font-medium hover:opacity-80 transition-opacity flex items-center gap-1"
                        aria-label={t('roomPage.upcoming')}
                      >
                        <CalendarClock size={14} className="shrink-0" /> <span className="hidden sm:inline">{t('roomPage.upcoming')}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => openPlayerModal(player, 'stats')}
                        className="p-1.5 sm:px-2 sm:py-1.5 rounded-md sm:rounded-lg text-violet-400 text-small font-medium hover:opacity-80 transition-opacity flex items-center gap-1"
                        aria-label={t('roomPage.stats')}
                      >
                        <BarChart3 size={14} className="shrink-0" /> <span className="hidden sm:inline">{t('roomPage.stats')}</span>
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <p className="text-sm text-muted-foreground/80 mt-4 text-center">
            {t('predictionsPage.demoPreviewHint')}
          </p>
        </div>
      </div>

      <BestOfModal
        isOpen={showBestOf}
        roomId={DEMO_ROOM_ID}
        onClose={() => setShowBestOf(false)}
        bestPredictionsFromApi={DEMO_BEST_PREDICTIONS}
      />

      <GamesAndPredictionsModal
        roomId={DEMO_ROOM_ID}
        isOpen={showUpcomingList}
        futureOnly
        demoMatches={DEMO_UPCOMING_MATCHES}
        onClose={() => setShowUpcomingList(false)}
        onSelectGame={(match) => {
          setSelectedMatch(match)
          setShowUpcomingList(false)
        }}
      />

      <GamesAndPredictionsModal
        roomId={DEMO_ROOM_ID}
        isOpen={showPlayedList}
        playedOnly
        titleKey="roomPage.played"
        demoMatches={DEMO_PLAYED_MATCHES}
        onClose={() => setShowPlayedList(false)}
        onSelectGame={(match) => {
          setSelectedMatch(match)
          setShowPlayedList(false)
        }}
      />

      <GamePredictionsModal
        roomId={DEMO_ROOM_ID}
        match={selectedMatch}
        isOpen={Boolean(selectedMatch)}
        demoPredictions={matchDemoPredictions}
        onClose={() => setSelectedMatch(null)}
      />

      {selectedPlayer && (
        <ViewPlayerStatsGamesModal
          player={selectedPlayer}
          isOpen={Boolean(selectedPlayer)}
          onClose={() => setSelectedPlayer(null)}
          initialViewType={modalViewType}
          roomId={DEMO_ROOM_ID}
          gameType="football"
          demoPlayerBundle={demoBundleForPlayer(selectedPlayer)}
        />
      )}

      {showPlay && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowPlay(false)}>
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setShowPlay(false)}
              className="absolute -top-1 right-0 sm:right-2 z-10 p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
              aria-label={t('stats.close')}
            >
              <X className="w-5 h-5" />
            </button>

            <div className="blue-sky-card rounded-2xl p-6 sm:p-8 text-center">
              <div className="flex items-center justify-between w-full mb-6">
                <div className="flex gap-1 sm:gap-1.5">
                  {Array.from({ length: Math.min(DEMO_PLAY_MATCH.totalSteps, 12) }).map((_, i) => (
                    <div
                      key={i}
                      className={`h-2 w-4 sm:w-5 rounded-sm flex-shrink-0 ${
                        i <= DEMO_PLAY_MATCH.stepIndex ? 'bg-primary' : 'bg-white/20'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-white/80 font-semibold text-small sm:text-body ml-2">
                  {DEMO_PLAY_MATCH.stepIndex + 1}/{DEMO_PLAY_MATCH.totalSteps}
                </span>
              </div>

              <div className="mb-4">
                <span className="text-4xl sm:text-5xl inline-block mb-2">⚽</span>
                <h1 className="text-xl sm:text-2xl font-bold text-white">{DEMO_PLAY_MATCH.number}</h1>
              </div>
              <p className="text-white/80 text-small sm:text-body mb-6 sm:mb-8">
                {formatDate(getMatchDate(DEMO_PLAY_MATCH, true), true)}
              </p>

              <div className="flex justify-between items-center mb-6 sm:mb-8 gap-4">
                <div className="flex-1 text-center min-w-0">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-xl sm:text-2xl">🏠</span>
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-white">
                    <TeamNameWithFlag
                      teamName={DEMO_PLAY_MATCH.teamA}
                      league={DEMO_PLAY_MATCH.league}
                      side="home"
                      nameClassName="text-base sm:text-lg font-semibold text-white"
                    />
                  </h3>
                </div>
                <div className="text-xl sm:text-2xl font-bold text-white flex-shrink-0">{t('predictions.vs')}</div>
                <div className="flex-1 text-center min-w-0">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-2">
                    <span className="text-xl sm:text-2xl">✈️</span>
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-white">
                    <TeamNameWithFlag
                      teamName={DEMO_PLAY_MATCH.teamB}
                      league={DEMO_PLAY_MATCH.league}
                      side="away"
                      nameClassName="text-base sm:text-lg font-semibold text-white"
                    />
                  </h3>
                </div>
              </div>

              <div className="space-y-3 sm:space-y-4">
                <button
                  type="button"
                  className="w-full px-6 py-3 sm:py-4 bg-primary hover:opacity-90 text-primary-foreground rounded-xl font-semibold transition-opacity text-small sm:text-body cursor-default"
                >
                  <VictoryTeamLabel
                    teamName={DEMO_PLAY_MATCH.teamA}
                    league={DEMO_PLAY_MATCH.league}
                    side="home"
                    nameClassName="text-small sm:text-body"
                  />
                </button>
                <button
                  type="button"
                  className="w-full px-6 py-3 sm:py-4 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all text-small sm:text-body cursor-default"
                >
                  {t('predictions.draw')}
                </button>
                <button
                  type="button"
                  className="w-full px-6 py-3 sm:py-4 bg-primary hover:opacity-90 text-primary-foreground rounded-xl font-semibold transition-opacity text-small sm:text-body cursor-default"
                >
                  <VictoryTeamLabel
                    teamName={DEMO_PLAY_MATCH.teamB}
                    league={DEMO_PLAY_MATCH.league}
                    side="away"
                    nameClassName="text-small sm:text-body"
                  />
                </button>
              </div>

              <div className="flex items-center justify-center gap-4 mt-6 sm:mt-8">
                <button
                  type="button"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                  aria-label="Next"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <RoomRulesModal
        isOpen={showRulesModal}
        roomId={DEMO_ROOM_ID}
        demoRoom={demoRoomForRules}
        onClose={() => setShowRulesModal(false)}
      />

      {adminMode && startingPointsPlayer && (
        <SetStartingPointsModal
          demoMode
          roomId={DEMO_ROOM_ID}
          player={startingPointsPlayer}
          onClose={() => setStartingPointsPlayer(null)}
          onSuccess={() => setStartingPointsPlayer(null)}
          onDemoSubmit={(signedAmount, note) => handleDemoStartingPoints(startingPointsPlayer, signedAmount, note)}
        />
      )}
    </>
  )
}

export default FootballRoomDemo
